/**
 * 2026.2 — REFERENCES (FY2026-27).
 *
 * Added: PCG / Determination 2026 confirming cents-per-km rises to 91c
 * for FY27 (88c for FY26 remains authoritative for historic stamps).
 * All other references carried forward from 2026.1 with FY27 phrasing.
 */

import type { AtoReferences } from "../types";

export const references: AtoReferences = {
  status: "confirmed",
  items: [
    // ============ Working from home ============
    {
      code: "PCG 2023/1",
      summary:
        "ATO Practical Compliance Guideline for the fixed-rate WFH method — 70c/hour covers energy, internet, phone and stationery; hours worked from home must be recorded contemporaneously.",
      url: "https://www.ato.gov.au/law/view/document?docid=COG/PCG20231/NAT/ATO/00001",
      topic: "wfh",
    },
    {
      code: "ATO — Fixed rate method (WFH)",
      summary:
        "Consumer-facing ATO page describing the WFH fixed-rate method + record-keeping requirements. Held at 70c/hour for FY26 and FY27.",
      url: "https://www.ato.gov.au/individuals-and-families/income-deductions-offsets-and-records/deductions-you-can-claim/work-related-deductions/working-from-home-expenses/fixed-rate-method",
      topic: "wfh",
    },

    // ============ Vehicle expenses ============
    {
      code: "PCG / Determination 2026 — cents-per-km",
      summary:
        "The Commissioner's determination raises the cents-per-km rate to 91c/km for FY26-27 (88c/km continues to apply for FY25-26 historic claims). Capped at 5,000 business km per car per income year.",
      url: "https://www.ato.gov.au/individuals-and-families/income-deductions-offsets-and-records/deductions-you-can-claim/work-related-deductions/cars-transport-and-travel/motor-vehicle-and-car-expenses/expenses-for-a-car-you-own-or-lease/cents-per-kilometre-method",
      topic: "vehicle",
    },
    {
      code: "ATO — Cents-per-kilometre method",
      summary:
        "Vehicle-expense method (up to 5,000 business km per car). 91c/km for FY26-27, 88c/km for FY25-26. Covers ALL running costs including depreciation.",
      url: "https://www.ato.gov.au/individuals-and-families/income-deductions-offsets-and-records/deductions-you-can-claim/work-related-deductions/cars-transport-and-travel/motor-vehicle-and-car-expenses/expenses-for-a-car-you-own-or-lease/cents-per-kilometre-method",
      topic: "vehicle",
    },
    {
      code: "ATO — Logbook method",
      summary:
        "Alternative to cents-per-km — claim actual business-use % of full car expenses. Requires 12-week logbook every 5 years + odometer records.",
      url: "https://www.ato.gov.au/individuals-and-families/income-deductions-offsets-and-records/deductions-you-can-claim/work-related-deductions/cars-transport-and-travel/motor-vehicle-and-car-expenses/expenses-for-a-car-you-own-or-lease/logbook-method",
      topic: "vehicle",
    },

    // ============ GST ============
    {
      code: "ATO — When to register for GST",
      summary:
        "$75,000/year turnover threshold (or projected) triggers compulsory GST registration for businesses; separate $150k applies to non-profits.",
      url: "https://www.ato.gov.au/businesses-and-organisations/gst-excise-and-indirect-taxes/gst/registering-for-gst",
      topic: "gst",
    },
    {
      code: "ATO — GST (10% overview)",
      summary:
        "10% GST applies to most goods, services and other items sold or consumed in Australia. Unchanged since 1 Jul 2000.",
      url: "https://www.ato.gov.au/businesses-and-organisations/gst-excise-and-indirect-taxes/gst",
      topic: "gst",
    },

    // ============ BAS ============
    {
      code: "ATO — Business Activity Statement",
      summary:
        "BAS reporting cycle (monthly, quarterly, annual) + G-labels for GST, 1A/1B, W1/W2 for PAYG-W, T1/T2/T3 for PAYG instalments.",
      url: "https://www.ato.gov.au/businesses-and-organisations/preparing-lodging-and-paying/business-activity-statements-bas",
      topic: "bas",
    },
    {
      code: "ATO — TPAR (Taxable Payments Annual Report)",
      summary:
        "Mandatory annual report for tradies + certain other industries reporting payments to contractors. Due 28 Aug each year.",
      url: "https://www.ato.gov.au/businesses-and-organisations/preparing-lodging-and-paying/reports-and-returns/taxable-payments-annual-report",
      topic: "bas",
    },

    // ============ Depreciation ============
    {
      code: "ATO — Instant asset write-off",
      summary:
        "Small businesses (aggregated turnover <$10M) can immediately deduct assets costing <$20,000 each. Held at $20,000 for FY27; confirm each budget cycle.",
      url: "https://www.ato.gov.au/businesses-and-organisations/income-deductions-and-concessions/depreciation-and-capital-expenses-and-allowances/simpler-depreciation-for-small-business/instant-asset-write-off",
      topic: "depreciation",
    },
    {
      code: "ATO — Simpler depreciation for small business",
      summary:
        "General small-business pool + instant asset write-off framework. Assets above the threshold are pooled at 15% (first year) / 30% (subsequent).",
      url: "https://www.ato.gov.au/businesses-and-organisations/income-deductions-and-concessions/depreciation-and-capital-expenses-and-allowances/simpler-depreciation-for-small-business",
      topic: "depreciation",
    },

    // ============ Record keeping ============
    {
      code: "ATO — Record-keeping for business",
      summary:
        "General 5-year record-keeping rule for tax purposes; records must be in English (or easily translatable) and unaltered.",
      url: "https://www.ato.gov.au/businesses-and-organisations/income-deductions-and-concessions/records-you-need-to-keep",
      topic: "records",
    },
    {
      code: "ATO — Small business entity concessions",
      summary:
        "Eligibility for the $10M aggregated-turnover SBE concessions (simplified depreciation, GST cash accounting, PAYG instalments).",
      url: "https://www.ato.gov.au/businesses-and-organisations/income-deductions-and-concessions/small-business-entity-concessions/eligibility",
      topic: "other",
    },
    {
      code: "ATO — Concessional super contributions cap",
      summary:
        "$32,500/year concessional cap for FY26-27 (was $30,000 for FY25-26). Covers SG + salary sacrifice + personal deductible contributions.",
      url: "https://www.ato.gov.au/individuals-and-families/super-for-individuals-and-families/super/growing-and-keeping-track-of-your-super/caps-limits-and-tax-on-super-contributions/concessional-contributions-cap",
      topic: "other",
    },
  ],
};
